// Голосовой ввод и вывод. Держим отдельно от компонентов: тут браузерные
// капризы, и им не место в разметке.
//
// Распознаёт один Deepgram, на сервере, по «Стоп». Раньше поверх него по ходу
// речи писался черновик от Web Speech API — и это оказалось вредно. Черновик
// калечил ровно то, ради чего всё затевалось: термины он превращал в кашу
// («dobox-инженер» вместо DevOps). Человек читал это прямо во время
// собственного ответа, сбивался с мысли и решал, что распознавание никуда не
// годится, — хотя точный текст приходил секундой позже и был нормальным.
//
// Вместо черновика — уровень звука с микрофона: он отвечает на единственный
// вопрос, который у говорящего есть по ходу, — слышно меня или нет.

/**
 * Голосовой ввод есть везде, где есть запись звука. Раньше здесь проверялся
 * Web Speech API, и Firefox с Safari получали «нужен Chrome» — хотя писать
 * звук и отправлять его на распознавание они умеют не хуже.
 */
export const voiceSupported = Boolean(
  navigator.mediaDevices?.getUserMedia && window.MediaRecorder);

/**
 * Контейнер записи выбирает браузер, а не мы.
 *
 * webm/opus знают Chrome и Firefox, а Safari — и любой iPhone — не знает вовсе:
 * MediaRecorder падал прямо в конструкторе, и голосовой ввод там не работал
 * совсем, с одним лишь «микрофон недоступен» на экране. Пустая строка значит
 * «пусть решает сам»: это лучше, чем упасть.
 */
const RECORD_MIME = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
  .find((type) => window.MediaRecorder?.isTypeSupported?.(type)) || '';

/**
 * Минимальный размер записи, который вообще имеет смысл распознавать.
 *
 * Порог низкий нарочно. При opus это примерно полсекунды звука: «да», «нет»,
 * «не знаю» — короткие, но это ответы, и терять их нельзя. Раньше порог был в
 * пять раз выше, и такой ответ молча исчезал: человек говорил, нажимал «Стоп»
 * и не получал ничего.
 */
const MIN_AUDIO_BYTES = 1500;

/**
 * Браузерное распознавание — третий движок, и работает он иначе двух остальных.
 *
 * Chrome умеет распознавать речь сам: стримит звук с микрофона на серверы
 * Google и возвращает текст. Бесплатно, без ключа, и — главное — ПАРАЛЛЕЛЬНО
 * записи: результат готов к моменту, когда человек жмёт «Стоп». К паузе после
 * «Стоп» это не добавляет ничего.
 *
 * Словаря у него нет вовсе: подсказать ему Promtail нечем, и термины он
 * перевирает гарантированно. Ценность его не в этом. Deepgram и Whisper
 * ошибаются в разных местах, и когда они расходятся, рассудить их нечем —
 * сшивка гадает по смыслу, и вот там-то она и теряла слова. Третий независимый
 * голос превращает догадку в арифметику: совпал с одним из двух — вопрос
 * закрыт (см. domain/stt-merge.ts).
 *
 * Показывать его человеку нельзя ни в каком виде: этой ошибкой мы уже
 * обжигались на черновике Web Speech, который писался поверх речи и сбивал с
 * мысли. Он уходит на сервер и остаётся внутри.
 */
const Recognizer = window.SpeechRecognition || window.webkitSpeechRecognition;

class BrowserEars {
  constructor() {
    this.rec = null;
    this.heard = '';
  }

  start() {
    if (!Recognizer) return;
    this.heard = '';
    try {
      const rec = new Recognizer();
      rec.lang = 'ru-RU';
      rec.continuous = true;
      // Промежуточные догадки нам не нужны: забираем только то, в чём движок
      // уверился окончательно.
      rec.interimResults = false;
      rec.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i += 1) {
          if (e.results[i].isFinal) this.heard += `${e.results[i][0].transcript} `;
        }
      };
      // Молчание, отказ в сети, любой сбой — это не повод ронять запись:
      // третий движок необязательный, без него связка просто работает как
      // раньше.
      rec.onerror = () => {};
      rec.start();
      this.rec = rec;
    } catch { this.rec = null; }
  }

  stop() {
    try { this.rec?.stop(); } catch { /* уже остановлен */ }
    this.rec = null;
    const text = this.heard.trim();
    this.heard = '';
    return text;
  }
}

/**
 * Заголовок с тем, что услышал браузер.
 *
 * Заголовком, а не параметром адреса: путь ручки должен оставаться цельной
 * строкой, иначе его перестаёт видеть карта связей проекта. Кириллица в
 * заголовках запрещена — уезжает в процентах, сервер разберёт обратно.
 */
export const heardHeader = (heard) =>
  (heard ? { 'x-heard': encodeURIComponent(String(heard).slice(0, 4000)) } : {});

/** Полос в столбике уровня: пять хватает, чтобы это читалось как живой звук. */
const BANDS = 5;

export class VoiceInput {
  /**
   * @param {(bands: number[]) => void} onLevel уровень звука, пять полос 0..1
   * @param {(msg: string, soft?: boolean) => void} onError
   *        soft — деградировала подсказка, запись при этом идёт
   */
  constructor(onLevel, onError) {
    this.onLevel = onLevel || (() => {});
    this.onError = onError;
    this.recorder = null;
    this.stream = null;
    this.chunks = [];
    this.listening = false;
    this.audioCtx = null;
    this.analyser = null;
    this.frame = 0;
    this.ears = new BrowserEars();
  }

  async start() {
    if (this.listening) return;
    this.listening = true;
    await this.startRecording();
  }

  /**
   * Столбик уровня. Не украшение: без черновика на экране это единственное,
   * что отвечает на «меня вообще слышно». Берём спектр, а не общую громкость —
   * по нему видно речь, а не гул вентилятора.
   */
  startMeter(stream) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.audioCtx = new Ctx();
      const source = this.audioCtx.createMediaStreamSource(stream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.6;
      source.connect(this.analyser);

      const bins = new Uint8Array(this.analyser.frequencyBinCount);
      // Речь живёт в нижней трети спектра — верх забирать незачем.
      const width = Math.max(1, Math.floor((bins.length / 3) / BANDS));
      const tick = () => {
        if (!this.listening || !this.analyser) return;
        this.analyser.getByteFrequencyData(bins);
        const bands = [];
        for (let b = 0; b < BANDS; b += 1) {
          let sum = 0;
          for (let i = b * width; i < (b + 1) * width; i += 1) sum += bins[i];
          bands.push(Math.min(1, (sum / width / 255) * 1.7));
        }
        this.onLevel(bands);
        this.frame = requestAnimationFrame(tick);
      };
      tick();
    } catch { /* без столбика переживём: запись важнее */ }
  }

  stopMeter() {
    cancelAnimationFrame(this.frame);
    this.analyser = null;
    this.audioCtx?.close?.().catch(() => {});
    this.audioCtx = null;
    this.onLevel([]);
  }

  async startRecording() {
    if (this.recorder) return;
    try {
      // Обработку звука просим выключить явно — браузер включает её сам, и
      // каждая её ступень что-то отрезает.
      //
      // Подавление шума оценивает шумовой фон и режет всё, что на него похоже.
      // Под нож попадает не шум: короткие тихие вставки посреди громкой речи.
      // Английский термин в русской фразе — ровно такая вставка, её
      // проговаривают быстрее и тише остального, и в записи она глохнет вместе
      // с ошибкой в расшифровке.
      //
      // Подавление эха здесь тоже незачем. Микрофон живёт только на время
      // ответа — трек останавливается сразу по «Стоп», — а интервьюер в это
      // время молчит: подавлять нечего, зато сам алгоритм звук портит.
      //
      // Автоусиление, наоборот, оставляем: оно тихую речь вытягивает, а не давит.
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true },
      });
      this.chunks = [];
      // Битрейт задаём сами: у части браузеров умолчание — 32 кбит/с, и на нём
      // термины в расшифровке плывут заметнее. Разница в размере записи
      // копеечная, в распознавании — нет.
      this.recorder = new MediaRecorder(this.stream, {
        ...(RECORD_MIME ? { mimeType: RECORD_MIME } : {}),
        audioBitsPerSecond: 96000,
      });
      this.recorder.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
      this.recorder.start();
      this.startMeter(this.stream);
      // Третий движок слушает параллельно записи: к паузе после «Стоп» он не
      // добавляет ничего, потому что к этому моменту уже всё услышал.
      this.ears.start();
    } catch (e) {
      this.onError(`микрофон недоступен (${e.name}) — отвечать придётся текстом`, false);
      this.recorder = null;
      // Микрофон мог уже открыться: getUserMedia отработал, а MediaRecorder
      // упал следом. Без этой строки поток оставался жить, «Стоп» до треков не
      // доходил (см. ниже, ранний выход), и микрофон висел включённым до
      // перезагрузки вкладки — с горящим индикатором записи.
      this.release();
    }
  }

  /**
   * Отпустить микрофон. Пока жив хоть один трек, браузер держит индикатор
   * записи, и человек имеет полное право считать, что его слушают.
   */
  release() {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  /**
   * Останавливает запись и отдаёт записанное.
   *
   * @returns {Promise<?{blob: Blob, heard: string}>} heard — что услышал
   *   браузер; пустая строка, если он не смог или его тут нет.
   */
  async stop() {
    this.listening = false;
    this.stopMeter();
    const heard = this.ears.stop();
    // Микрофон отпускаем в любом случае, а не только когда есть что отдать.
    if (!this.recorder) {
      this.release();
      return null;
    }

    // Тип берём у самого рекордера: он мог выбрать не то, что мы просили, а
    // сервер по этому заголовку понимает, что отдавать распознаванию.
    const type = this.recorder.mimeType || RECORD_MIME || 'audio/webm';
    const blob = await new Promise((resolve) => {
      this.recorder.onstop = () => resolve(new Blob(this.chunks, { type }));
      try { this.recorder.stop(); } catch { resolve(new Blob()); }
    });
    this.release();
    this.recorder = null;
    return blob.size < MIN_AUDIO_BYTES ? null : { blob, heard };
  }
}

// ── озвучка ────────────────────────────────────────────────────────────────

/**
 * Реплику режем на предложения: первое начинает играть, пока синтезируются
 * остальные. Иначе ждём генерацию всего текста и слушаем тишину.
 */
export function splitSentences(text) {
  return text.split(/(?<=[.!?…])\s+/).reduce((acc, s) => {
    const last = acc[acc.length - 1];
    if (last && (last + ' ' + s).length < 150) acc[acc.length - 1] = last + ' ' + s;
    else acc.push(s);
    return acc;
  }, []).filter((s) => s.trim());
}

export class Voice {
  constructor() {
    this.audio = null;
    this.generation = 0;
  }

  /** @param {(part: string) => string} urlFor */
  async speak(text, urlFor) {
    const mine = ++this.generation;
    const parts = splitSentences(text);
    if (!parts.length) return;

    const fetchPart = async (part) => {
      const res = await fetch(urlFor(part));
      if (!res.ok) throw new Error('синтез недоступен');
      return URL.createObjectURL(await res.blob());
    };

    let next = fetchPart(parts[0]);
    for (let i = 0; i < parts.length; i++) {
      const url = await next;
      if (mine !== this.generation) return;              // перебили
      next = parts[i + 1] ? fetchPart(parts[i + 1]) : null;
      await this.play(url);
      if (mine !== this.generation) return;
    }
  }

  play(url) {
    return new Promise((resolve, reject) => {
      this.audio = new Audio(url);
      this.audio.onended = resolve;
      this.audio.onerror = reject;
      this.audio.play().catch(reject);
    });
  }

  stop() {
    this.generation++;
    if (this.audio) { this.audio.pause(); this.audio = null; }
    window.speechSynthesis?.cancel();
  }

  /** Запасной вариант, если сервер не смог синтезировать. */
  browserSay(text, onDone) {
    if (!window.speechSynthesis) return onDone();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'ru-RU';
    u.rate = 1.15;
    u.onend = onDone;
    u.onerror = onDone;
    window.speechSynthesis.speak(u);
  }
}
